import { useMemo, useRef } from 'react';
import { ColDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { GrcGrid, gridOptions, DomLayout } from '@grc/ui-package';
import { useI18nState } from '../../../../../../stores/settings/language-context';
import { IShipmentHandlingUnitRow } from '../../../../../../models/shipments/shipment-details.interface';
import {
  formatDimensions,
  formatUnitValue,
  UnitSymbolMap
} from '../useDetailsTab';
import './handling-unit-table.scss';

interface IHandlingUnitTableProps {
  shipmentNumber: string;
  rows: IShipmentHandlingUnitRow[];
  totalQuantity: number;
  totalWeightLabel: string;
  unitSymbolMap: UnitSymbolMap;
  resolveHandlingUnitTypeName: (row: IShipmentHandlingUnitRow) => string;
  resolveNmfcClassName: (row: IShipmentHandlingUnitRow) => string;
}

// Read-only port of the reference Order app's HandlingUnitDetails grid -
// same column set, minus the Actions/delete column. Hazmat/temperature/
// non-stackable render as plain tags in the Type column rather than
// clickable popups (those popups carry edit-only setters in the reference,
// and no mockup shows their content).
export default function HandlingUnitTable({
  shipmentNumber,
  rows,
  totalQuantity,
  totalWeightLabel,
  unitSymbolMap,
  resolveHandlingUnitTypeName,
  resolveNmfcClassName
}: IHandlingUnitTableProps) {
  const gridRef = useRef<AgGridReact>(null);
  const { localeData } = useI18nState();

  const colDefs: ColDef<IShipmentHandlingUnitRow>[] = useMemo(
    () => [
      {
        headerName: localeData.HUType,
        filter: false,
        sortable: false,
        valueGetter: params => (params.data ? resolveHandlingUnitTypeName(params.data) : '-')
      },
      {
        headerName: localeData.HuId,
        colId: 'huId',
        filter: false,
        sortable: false,
        minWidth: 140,
        valueGetter: params =>
          params.data?.sequenceNumber != null
            ? `${shipmentNumber} - ${params.data.sequenceNumber}`
            : '-'
      },
      {
        headerName: `${localeData.Dimensions}(LxWxH)`,
        colId: 'dimensions',
        minWidth: 170,
        filter: false,
        sortable: false,
        valueGetter: params =>
          params.data ? formatDimensions(params.data, unitSymbolMap) : '-'
      },
      {
        headerName: localeData.Weight,
        filter: false,
        sortable: false,
        maxWidth: 110,
        valueGetter: params => formatUnitValue(params.data?.weight, unitSymbolMap)
      },
      {
        headerName: localeData.NMFC,
        field: 'nmfcCode',
        filter: false,
        sortable: false,
        maxWidth: 120
      },
      {
        headerName: localeData.NMFCclass,
        maxWidth: 120,
        filter: false,
        sortable: false,
        valueGetter: params => (params.data ? resolveNmfcClassName(params.data) : '-')
      },
      {
        headerName: localeData.HUDescription,
        field: 'productDescription',
        filter: false,
        sortable: false
      },
      {
        headerName: localeData.SpecialHandling,
        filter: false,
        sortable: false,
        minWidth: 180,
        valueGetter: params => {
          const data = params.data;
          if (!data) return '-';
          const labels: string[] = [];
          if (data.hasHazmat) labels.push(localeData.Hazmat);
          if (data.hasTemperatureControl)
            labels.push(localeData.TemperatureControlled);
          if (data.isNonStackable) labels.push(localeData.NonStackable);
          return labels.length ? labels.join(', ') : '-';
        }
      }
    ],
    [
      localeData,
      resolveHandlingUnitTypeName,
      resolveNmfcClassName,
      shipmentNumber,
      unitSymbolMap
    ]
  );

  return (
    <div className="handling-unit-table">
      <label className="grc-card-title ml-2">
        {localeData.HandlingUnitDetails}
      </label>
      <div className="handling-unit-table-grid grc-grid-no-col-filters">
        <GrcGrid<any>
          gridRef={gridRef}
          gridOptions={gridOptions}
          colDefs={colDefs}
          domLayout={DomLayout.AutoHeight}
          rowData={rows}
        />
      </div>
      <div className="d-flex justify-content-center mt-2">
        <label className="mr-4">
          {localeData.TotalQuantity} :
          <span className="ml-2 handling-value-txt">{totalQuantity}</span>
        </label>
        <label>
          {localeData.TotalWeight} :
          <span className="ml-2 handling-value-txt">{totalWeightLabel}</span>
        </label>
      </div>
    </div>
  );
}
